import React from "react";

import { Button, SectionCard } from "@amphore-dev/amphore-lib";
import i18n, { t } from "i18next";
import { useNavigate } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import InvoicesService from "@/services/InvoicesService";

import { InvoiceStatusBadge } from "@/components/molecules/InvoiceStatusBadge/InvoiceStatusBadge";
import { PlanGate } from "@/components/organisms/PlanGate/PlanGate";

import { invoiceKeys } from "@/constants/CQueryKeys";

import { TSavedReport } from "@/types";

import { formatMoney } from "@/utils";

interface IReportInvoicesCardProps {
	report: TSavedReport;
	className?: string;
}

/**
 * On a finalized report: the invoices already drawn from it, and the
 * button that starts a new one (`/invoices/new?reportId=`). A draft
 * cannot be invoiced, so the card is not shown for one.
 */
export const ReportInvoicesCard: React.FC<IReportInvoicesCardProps> = ({
	report,
	className,
}) => {
	const navigate = useNavigate();

	const { data, isLoading } = useQuery({
		queryKey: invoiceKeys.ofReport(report.id),
		queryFn: () => InvoicesService.list({ reportId: report.id, limit: 50 }),
		enabled: !report.isDraft,
	});

	if (report.isDraft) return null;

	const invoices = data?.data ?? [];

	return (
		<SectionCard
			title={t("invoices.linkedInvoices.title")}
			className={className}
			actions={
				<PlanGate on="invoices">
					<Button
						size="sm"
						picto="fileText"
						onClick={() =>
							navigate(`/invoices/new?reportId=${report.id}`)
						}
					>
						{t("invoices.linkedInvoices.invoiceThis")}
					</Button>
				</PlanGate>
			}
		>
			{!isLoading && invoices.length === 0 ? (
				<p className="text-sm text-neutral-500">
					{t("invoices.linkedInvoices.none")}
				</p>
			) : (
				<ul className="flex flex-col divide-y divide-neutral-100">
					{invoices.map((invoice) => (
						<li key={invoice.id}>
							<button
								type="button"
								className="flex w-full items-center justify-between gap-3 py-2 text-left text-sm hover:bg-neutral-50"
								onClick={() =>
									navigate(`/invoices/${invoice.id}`)
								}
							>
								<span className="flex items-center gap-2">
									<span
										className={
											invoice.number
												? "font-medium"
												: "text-neutral-400"
										}
									>
										{invoice.number ?? t("invoices.draft")}
									</span>
									<InvoiceStatusBadge invoice={invoice} />
								</span>
								<span className="tabular-nums">
									{formatMoney(
										invoice.totalTtc,
										invoice.currency,
										i18n.language
									)}
								</span>
							</button>
						</li>
					))}
				</ul>
			)}
		</SectionCard>
	);
};
